import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, TextField, Button, CircularProgress, TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, Alert, Checkbox, FormControlLabel } from '@mui/material';
import RegistrationDetailsDialog from './RegistrationDetailsDialog';
import { getRegistrationsByEdition, generateTestRegistrations, resetEditionRegistrations } from '../../services/registrationService';
import { syncUsersFromRegistrations } from '../../utils/userSyncUtils';
import { listCodeList } from '../../services/codeListService';
import { Registration } from '../../types';
import { useEventEdition } from '../../contexts/EventEditionContext';

// Replacement for the deleted statusService
interface RegistrationStatus {
  id: string;
  label: string;
}

const RegistrationsPanel: React.FC = () => {
  const { event: selectedEvent } = useEventEdition();
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [statuses, setStatuses] = useState<RegistrationStatus[]>([]);
  const [regLoading, setRegLoading] = useState(false);
  const [testCount, setTestCount] = useState<number>(0);
  const [clearBeforeGenerate, setClearBeforeGenerate] = useState(false);
  const [useRealUsers, setUseRealUsers] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);

  // State for details dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedReg, setSelectedReg] = useState<Registration | null>(null);

  // Load registrations and statuses
  const loadData = useCallback(async () => {
    if (!selectedEvent?.id) return;
    setRegLoading(true);
    try {
      const regs = await getRegistrationsByEdition(selectedEvent.id);
      // sort by registrationNumber ascending
      regs.sort((a, b) => Number(a.registrationNumber) - Number(b.registrationNumber));
      // Get statuses from codeLists collection
      const codeListItems = await listCodeList('status', 'registrations');
      // Map to the expected RegistrationStatus interface
      const sts = codeListItems.map(item => ({
        id: item.id,
        label: item.code
      }));
      setRegistrations(regs);
      setStatuses(sts);
    } catch (error) {
      console.error('Error loading registrations:', error);
    } finally {
      setRegLoading(false);
    }
  }, [selectedEvent?.id]);

  // Load data when selected event changes
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handlers for details dialog
  const openDetails = (reg: Registration) => {
    setSelectedReg(reg);
    setDialogOpen(true);
  };
  const closeDetails = () => {
    setDialogOpen(false);
    setSelectedReg(null);
    // Refresh list on close to reflect any changes made
    loadData();
  };
  const handleUpdate = () => { loadData(); };

  const handleSyncUsers = async () => {
    if (!selectedEvent?.id) return;
    setSyncLoading(true);
    try {
      await syncUsersFromRegistrations(selectedEvent.id);
    } catch (error) {
      console.error('Error syncing users from registrations:', error);
    } finally {
      setSyncLoading(false);
    }
  };

  const [testResult, setTestResult] = useState<{ created?: number; fromRealUsers?: boolean } | null>(null);

  const handleGenerateTest = async () => {
    if (!selectedEvent?.id) return;
    setTestLoading(true);
    setTestResult(null);
    try {
      // eslint-disable-next-line no-console
      console.log('Generating test registrations:', { testCount, clearBeforeGenerate, useRealUsers });
      const result = await generateTestRegistrations(selectedEvent.id, testCount, clearBeforeGenerate, useRealUsers);
      // eslint-disable-next-line no-console
      console.log('Generation result:', result);
      setTestResult({ created: result.created, fromRealUsers: result.fromRealUsers });
      const regs = await getRegistrationsByEdition(selectedEvent.id);
      // sort by registrationNumber ascending
      regs.sort((a, b) => Number(a.registrationNumber) - Number(b.registrationNumber));
      setRegistrations(regs);
    } catch (error) {
      console.error('Error generating test data:', error);
    } finally {
      setTestLoading(false);
    }
  };

  const handleResetRegistrations = async () => {
    if (!selectedEvent?.id) return;
    const confirmed = window.confirm(
      `Reset registrations for ${selectedEvent.id}? This deletes all registrations and public registrations for this edition and resets the registration counter.`
    );
    if (!confirmed) return;

    setResetLoading(true);
    try {
      await resetEditionRegistrations(selectedEvent.id);
      const regs = await getRegistrationsByEdition(selectedEvent.id);
      regs.sort((a, b) => Number(a.registrationNumber) - Number(b.registrationNumber));
      setRegistrations(regs);
    } catch (error) {
      console.error('Error resetting registrations:', error);
    } finally {
      setResetLoading(false);
    }
  };

  // registration summary counts
  const now = new Date();
  const participants = registrations.filter(r => !r.isOnWaitinglist);
  const participantsPending = participants.filter(r => r.status === 'pending').length;
  const participantsConfirmed = participants.filter(r => r.status === 'confirmed').length;
  const waitingList = registrations.filter(r => r.isOnWaitinglist);
  const waitingPending = waitingList.filter(r => r.status === 'pending').length;
  const waitingConfirmed = waitingList.filter(r => r.status === 'confirmed').length;
  const cancelledCount = registrations.filter(r => r.status === 'cancelled').length;
  const expiredCount = registrations.filter(r => r.status === 'expired').length;
  
  // Warning counts
  // 1. WL registrations where expiration is in the past AND status != 'expired'
  const wlExpiredNotMarked = registrations.filter(r => 
    r.isOnWaitinglist && 
    r.waitinglistExpires && 
    (r.waitinglistExpires as any) <= now && 
    r.status !== 'expired'
  ).length;
  
  // 2. Registrations with status='expired' AND paymentMade > 0
  const expiredWithPayment = registrations.filter(r => 
    r.status === 'expired' && 
    (r.paymentMade || 0) > 0
  ).length;
  
  const cancelledWithPayment = registrations.filter(r => 
    r.isOnWaitinglist && 
    r.status === 'cancelled' && 
    (r.paymentMade || 0) > 0
  ).length;

  const totalWarnings = wlExpiredNotMarked + expiredWithPayment + cancelledWithPayment;

  const formatWaitinglistExpires = (value: any): string => {
    if (!value) return '-';
    try {
      if (typeof value.toDate === 'function') {
        return value.toDate().toLocaleString();
      }
      if (value instanceof Date) {
        return value.toLocaleString();
      }
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed.toLocaleString();
      }
      return String(value);
    } catch (err) {
      console.warn('Unable to format waitinglistExpires value', value, err);
      return '-';
    }
  };
  if (!selectedEvent) {
    return (
      <Box p={3}>
        <Alert severity="info">Please select an event edition to view registrations.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Registrations & Payment Status</Typography>
        <Typography variant="subtitle1" color="textSecondary">
          {selectedEvent.eventName} {selectedEvent.edition}
        </Typography>
      </Box>
      {totalWarnings > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Action required:</strong>
          <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
            {wlExpiredNotMarked > 0 && (
              <li>{wlExpiredNotMarked} waiting-list registration{wlExpiredNotMarked !== 1 ? 's' : ''} have passed their expiration and should be marked as expired.</li>
            )}
            {expiredWithPayment > 0 && (
              <li>{expiredWithPayment} expired registration{expiredWithPayment !== 1 ? 's' : ''} with payment made should be reviewed for refund.</li>
            )}
            {cancelledWithPayment > 0 && (
              <li>{cancelledWithPayment} cancelled waiting-list registration{cancelledWithPayment !== 1 ? 's' : ''} with payment made should be reviewed for refund.</li>
            )}
          </ul>
        </Alert>
      )}
      <TableContainer component={Paper} sx={{ mt: 2, mb: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Participants</TableCell>
              <TableCell>Waiting-list</TableCell>
              <TableCell>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Pending</TableCell>
              <TableCell>{participantsPending}</TableCell>
              <TableCell>{waitingPending}</TableCell>
              <TableCell>{participantsPending + waitingPending}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Confirmed</TableCell>
              <TableCell>{participantsConfirmed}</TableCell>
              <TableCell>{waitingConfirmed}</TableCell>
              <TableCell>{participantsConfirmed + waitingConfirmed}</TableCell>
            </TableRow>
            <TableRow 
              sx={{ 
                position: 'sticky',
                top: '48px',
                zIndex: 2,
                backgroundColor: 'background.paper',
                borderBottom: '1px solid',
                borderColor: 'divider',
                '& th, & td': {
                  backgroundColor: 'inherit'
                }
              }}
            >
              <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Sum active reg.</TableCell>
              <TableCell>{participantsPending + participantsConfirmed}</TableCell>
              <TableCell>{waitingPending + waitingConfirmed}</TableCell>
              <TableCell>{participantsPending + participantsConfirmed + waitingPending + waitingConfirmed}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Cancelled</TableCell>
              <TableCell>{registrations.filter(r => !r.isOnWaitinglist && r.status==='cancelled').length}</TableCell>
              <TableCell>{registrations.filter(r => r.isOnWaitinglist && r.status==='cancelled').length}</TableCell>
              <TableCell>{cancelledCount}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Expired</TableCell>
              <TableCell>{registrations.filter(r => !r.isOnWaitinglist && r.status==='expired').length}</TableCell>
              <TableCell>{registrations.filter(r => r.isOnWaitinglist && r.status==='expired').length}</TableCell>
              <TableCell>{expiredCount}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <TextField
          type="number"
          label="Number of test registrations"
          value={testCount}
          onChange={(e) => setTestCount(parseInt(e.target.value) || 0)}
          size="small"
          sx={{ width: 250 }}
          disabled={!selectedEvent.id}
        />
        <FormControlLabel
          control={(
            <Checkbox
              checked={clearBeforeGenerate}
              onChange={(e) => setClearBeforeGenerate(e.target.checked)}
              disabled={testLoading || resetLoading}
            />
          )}
          label="Clear existing before generate"
        />
        <FormControlLabel
          control={(
            <Checkbox
              checked={useRealUsers}
              onChange={(e) => setUseRealUsers(e.target.checked)}
              disabled={testLoading || resetLoading}
            />
          )}
          label="Use real users (with age/gender)"
        />
        <Button variant="contained" onClick={handleGenerateTest} disabled={!testCount || testLoading || resetLoading}>
          {testLoading ? <CircularProgress size={20} /> : 'Generate Test Registrations'}
        </Button>
        <Button
          variant="outlined"
          color="warning"
          onClick={handleResetRegistrations}
          disabled={!selectedEvent?.id || resetLoading || testLoading}
        >
          {resetLoading ? <CircularProgress size={20} /> : 'Reset registrations for edition'}
        </Button>
        <Button variant="outlined" onClick={handleSyncUsers} disabled={!selectedEvent?.id || syncLoading || testLoading || resetLoading}>
          {syncLoading ? <CircularProgress size={20} /> : 'Sync users from registrations'}
        </Button>
      </Box>
      {testResult && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Generated <strong>{testResult.created}</strong> test registrations
          {testResult.fromRealUsers ? ' from real users (with age/gender data)' : ' (random data)'}
        </Alert>
      )}
      {regLoading ? (
        <CircularProgress />
      ) : (
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Class</TableCell>
              <TableCell>Paid</TableCell>
              <TableCell>List</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>WL Expires</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {registrations.map(reg => {
              // Warning flags
              const isWlExpired = !!(reg.isOnWaitinglist && reg.waitinglistExpires && (reg.waitinglistExpires as any) <= now && reg.status !== 'expired');
              const hasExpiredWithPayment = !!(reg.status === 'expired' && (reg.paymentMade || 0) > 0);
              const hasCancelledWlWithPayment = !!(reg.isOnWaitinglist && reg.status === 'cancelled' && (reg.paymentMade || 0) > 0);
              const hasAnyWarning = isWlExpired || hasExpiredWithPayment || hasCancelledWlWithPayment;
              
              return (
              <TableRow key={reg.id} sx={hasAnyWarning ? { backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(244,67,54,0.18)' : 'rgba(244,67,54,0.08)' } : undefined}>
                <TableCell>{reg.registrationNumber}</TableCell>
                <TableCell>{reg.firstName} {reg.lastName}</TableCell>
                <TableCell>{reg.raceDistance}</TableCell>
                <TableCell>{reg.paymentMade}</TableCell>
                <TableCell>{reg.isOnWaitinglist ? 'Waiting-list' : 'Participant'}</TableCell>
                <TableCell>
                  <Box component="span" sx={hasAnyWarning ? { color: (theme) => theme.palette.error.main, fontWeight: 700 } : undefined}>
                    {reg.status}
                  </Box>
                </TableCell>
                <TableCell>
                  {isWlExpired && (
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'error.main', fontWeight: 500 }}>
                      WL expired
                    </Typography>
                  )}
                  {hasExpiredWithPayment && (
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'error.main', fontWeight: 500 }}>
                      Expired + paid
                    </Typography>
                  )}
                  {hasCancelledWlWithPayment && (
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'error.main', fontWeight: 500 }}>
                      Cancelled WL + paid
                    </Typography>
                  )}
                  {!hasAnyWarning && reg.waitinglistExpires && (
                    formatWaitinglistExpires(reg.waitinglistExpires)
                  )}
                  {!hasAnyWarning && !reg.waitinglistExpires && '-'}
                </TableCell>
                <TableCell>
                  <Button size="small" onClick={() => openDetails(reg)}>Details</Button>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      )}
      {selectedReg && (
        <RegistrationDetailsDialog
          open={dialogOpen}
          registration={selectedReg}
          statuses={statuses}
          onClose={closeDetails}
          onUpdate={handleUpdate}
        />
      )}
    </Box>
  );
};

export default RegistrationsPanel;
